const TRANSLATOR_VERTICAL_ALIGN = 'vertical-align:inherit';

/**
 * True for the `<font>` Chrome inserts when it translates a text node.
 * Other `<font>` elements are left in place.
 */
const isBrowserTranslatorFont = (node: Node): node is HTMLElement => {
  if (!(node instanceof HTMLElement) || node.tagName !== 'FONT') return false;
  const declared = (node.getAttribute('style') ?? '')
    .toLowerCase()
    .replace(/\s+/g, '');
  if (declared.includes(TRANSLATOR_VERTICAL_ALIGN)) return true;
  return node.style.verticalAlign === 'inherit';
};

/**
 * Puts a translator `<font>`'s children back on its parent and removes the font.
 * Nested translator fonts are unwrapped first so the original text node is a
 * direct child of the element React rendered.
 */
const unwrapBrowserTranslatorFont = (font: HTMLElement): void => {
  const parent = font.parentNode;
  if (!parent) return;

  const nested = [...font.querySelectorAll('font')].filter(
    isBrowserTranslatorFont,
  );
  for (const inner of nested.reverse()) unwrapBrowserTranslatorFont(inner);

  while (font.firstChild) parent.insertBefore(font.firstChild, font);
  parent.removeChild(font);
};

const collectTranslatorFonts = (node: Node, into: HTMLElement[]): void => {
  if (isBrowserTranslatorFont(node)) into.push(node);
  if (!(node instanceof Element)) return;
  for (const font of node.querySelectorAll('font')) {
    if (isBrowserTranslatorFont(font)) into.push(font);
  }
};

const ensureNoTranslateMeta = (doc: Document): void => {
  const existing = doc.head.querySelector('meta[name="google"]');
  if (existing instanceof HTMLMetaElement) {
    existing.content = 'notranslate';
    return;
  }
  const meta = doc.createElement('meta');
  meta.name = 'google';
  meta.content = 'notranslate';
  doc.head.appendChild(meta);
};

/**
 * Stops the browser from rewriting React-owned text, and undoes it if a
 * translation still wraps those nodes.
 *
 * Chrome nests translated text in `<font style="vertical-align: inherit">`.
 * React then throws `NotFoundError` from `removeChild` on the next update that
 * deletes that text. `translate="no"` and the Google `notranslate` meta ask the
 * browser not to translate. Chrome 138 and newer can still show the translate
 * bar with both set, so the observer is what puts the original text node back
 * under the element React rendered.
 *
 * @returns Disconnects the observer.
 */
export const installBrowserTranslationGuard = (): (() => void) => {
  const root = document.documentElement;
  root.setAttribute('translate', 'no');
  ensureNoTranslateMeta(document);

  const observer = new MutationObserver((mutations) => {
    const fonts: HTMLElement[] = [];
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        collectTranslatorFonts(node, fonts);
      }
    }
    if (fonts.length === 0) return;

    // Innermost fonts first so a nested wrap collapses in one pass.
    // Echo records from this unwrap arrive later and are not translator fonts.
    for (const font of fonts.reverse()) {
      if (font.isConnected) unwrapBrowserTranslatorFont(font);
    }
  });
  observer.observe(root, { childList: true, subtree: true });
  return () => observer.disconnect();
};
